import cx from 'classnames';
import { get, set } from '../vendor/idb-keyval';
import { h } from 'preact';

// components
import Typeahead from './Typeahead';
import PureComponent from './PureComponent';
import Portal from './Portal';
import Ballots from './Ballots';
import CandidateRow from './CandidateRow';
import EachPartyColumn from './EachPartyColumn';
import Address from './Address';

// local
import debounce from '../utils/debounce';
import { lookup } from '../utils/legislative-districts';
import find from '../utils/find';
import { determineBallotTitle } from '../utils/election-utils';
import loadAssetURL from '../utils/load-asset-url';

const googleIMG = loadAssetURL('powered-by-google');

const sessionToken = new google.maps.places.AutocompleteSessionToken();
const autocompleteService = new google.maps.places.AutocompleteService();

const bounds = {
  east: -93.514764,
  west: -106.662177,
  south: 25.843902,
  north: 36.500199,
};

const componentRestrictions = { country: 'us' };
const types = ['geocode'];

const getPredictions = input => {
  return new Promise((resolve, reject) => {
    autocompleteService.getPlacePredictions(
      { bounds, componentRestrictions, input, sessionToken, types },
      (predict, status) => {
        if (status === 'OK') resolve(predict);

        reject(new Error('No results found.'));
      }
    );
  });
};

const inputProps = {
  id: 'address-input',
  class: 'address-input__input',
  placeholder: '1010 Colorado St, Austin, TX, 78701',
};

// always render what's provided, the geocoder decides
const shouldItemRender = () => true;

class FindYourDistricts extends PureComponent {
  constructor() {
    super();

    this.state = {
      coordinates: null,
      data: null,
      lookupError: false,
      lookupPending: false,
      lookedFoundStored: false,
      results: [],
      value: '',
    };

    this.handleGeocode = this.handleGeocode.bind(this);
    this.debouncedHandleGeocode = debounce(this.handleGeocode, 300);
    this.onChange = this.onChange.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onSelect = this.onSelect.bind(this);
  }

  onClear(value) {
    this.setState({ value: '' });
  }

  onChange(value) {
    this.setState({ value }, this.debouncedHandleGeocode);
  }

  handleGeocode() {
    const { value: input } = this.state;

    getPredictions(input)
      .then(results => this.setState({ results }))
      .catch(() => this.setState({ results: [] }));
  }

  onSelect({ name, placeId }) {
    const { onSelect } = this.props;
    this.setState({ lookupError: false, lookupPending: true, value: name });

    this.placesService.getDetails(
      {
        fields: ['geometry.location'],
        placeId,
        sessionToken,
      },
      result => {
        const {
          lat: latitude,
          lng: longitude,
        } = result.geometry.location.toJSON();

        const coordinates = { latitude, longitude };

        lookup(coordinates)
          .then(({ data }) =>
            this.setState(
              {
                coordinates,
                data,
                lookupPending: false,
                lookedFoundStored: false,
              }
              // () => set('your-races-lookup', data)
            )
          )
          .catch(() =>
            this.setState({
              data: null,
              lookupError: true,
              lookupPending: false,
            })
          );
      }
    );
  }

  componentDidMount() {
    this.placesService = new google.maps.places.PlacesService(this.inputRef);

    // get('your-races-lookup').then(data => {
    //   if (data != null) {
    //     this.setState({ data, lookedFoundStored: true });
    //   }
    // });
  }

  render() {
    const { contests, notes } = this.props;

    const {
      coordinates,
      data,
      lookupError,
      lookupPending,
      lookedFoundStored,
      results,
      value,
    } = this.state;

    let addressFeedback = null;

    const matches = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };


    const matchNotes = {
      'us-house': null,
      sboe: null,
      'tx-senate': null,
      'tx-house': null,
    };

    if (data) {
      matches['us-house'] = runMatch(
        contests,
        'U.S. House',
        data.us_house.district
      );
      matches['sboe'] = runMatch(
        contests,
        'State Board Of Education',
        data.sboe.district
      );
      matches['tx-senate'] = runMatch(
        contests,
        'Texas Senate',
        data.tx_senate.district
      );
      matches['tx-house'] = runMatch(
        contests,
        'Texas House',
        data.tx_house.district
      );

      matchNotes['us-house'] = getNotes(
        data.us_house.district,
        'us-house',
        notes
      );

      matchNotes['sboe'] = getNotes(
        data.sboe.district,
        'state-board-of-education',
        notes
      );

      matchNotes['tx-senate'] = getNotes(
        data.tx_senate.district,
        'tx-senate',
        notes
      );

      matchNotes['tx-house'] = getNotes(
        data.tx_house.district,
        'tx-house',
        notes
      );
    }

    function runMatch(races, chamber, district) {
      var first_level;
      first_level = findByKey(races, chamber);
      return findByKey(first_level.values, district);
    }

    function findByKey(o, id) {
      var o2 = find(o, contest => contest.key === id.toString());
      return o2;
    }

    function getNotes(data, chamber, notesData) {
      const NUMERIC_REGEXP = /\d+$/; // Get the numberic district numbers
      const CHAMBER_REGEXP = /^.*(?=(\D))/; // Get the slugs for the chambers, should match the variable chamber

      // Get the regexed versions of the chambers
      const regexChamber = notesData.map(x => x.type.match(CHAMBER_REGEXP));

      // filter out the chamber in question
      const filtered = regexChamber.filter(word => word[0] === chamber);

      //Only look for district numbers in the filtered out chamber array
      const regexNum = filtered.map(x => x.input.match(NUMERIC_REGEXP));

      // Find the matching distric numbers for each district in the array, there should only be one!!!
      const notesRace = find(regexNum, contest => contest[0] === String(data));

      //Find the notes text that matches that one;
      const notesText = find(
        notesData,
        contest => notesRace && contest.type === notesRace.input
      );

      return [notesRace, notesText];
    }

    if (lookupPending) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          Finding your details...{' '}
          <svg class="icon icon-spinner animate-pulse">
            <use xlinkHref="#icon-spinner" />
          </svg>
        </p>
      );
    }

    if (lookupError) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          <br></br>
          Hmm... we were unable to find you in Texas. Please check your address
          and try again!
        </p>
      );
    }

    if (coordinates && !lookupPending && !lookupError) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          <br></br>
          We found your information! The ballot information has been
          personalized for you.
          <br></br>
        </p>
      );
    }

    if (lookedFoundStored && !lookupPending && !lookupError) {
      addressFeedback = (
        <p class="has-padding t-serif t-weight-bold t-align-center">
          Welcome back! Your browser cached the address you looked up on your
          last visit. Here are the districts we told you about last time.
        </p>
      );
    }

    return (
      <div class="address-input t-sans">
        <svg class="icon icon-drawn-left-arrow">
          <use xlinkHref="#icon-drawn-left-arrow"></use>
        </svg>
        <label class="address-input__label" for="address-input">
          Enter your address
        </label>
        <Typeahead
          inputProps={inputProps}
          items={results}
          getItemValue={item => ({
            name: item.description,
            placeId: item.place_id,
          })}
          value={value}
          onChange={this.onChange}
          onClear={this.onClear}
          onSelect={this.onSelect}
          renderItem={(item, isHighlighted) => (
            <li
              class={cx('listbox__item', {
                'listbox__item--highlighted': isHighlighted,
              })}
              key={item.id}
            >
              {item.description}
            </li>
          )}
          renderAttribution={() => (
            <li class="listbox__item listbox__item--attribution">
              <img src={googleIMG} alt="Powered by Google" />
            </li>
          )}
          shouldItemRender={shouldItemRender}
          renderInput={props => (
            <input
              {...props}
              ref={el => {
                props.ref(el);
                this.inputRef = el;
              }}
            />
          )}
        />
        {addressFeedback}
        <Portal node={document.getElementById('local-candidates-info')}>
          {data && data.county && (
            <span>
              <Address
                key={data.county.fips}
                county={data.county.name}
                fips={data.county.fips}
              />
            </span>
          )}
        </Portal>
        {data && contests.length > 0 && (
          <Portal
            node={document.getElementById('your-races-results')}
            ref={ref => (this._resultsPortal = ref)}
          >
            <div class="representative-districts contest-list">
              <Ballots
                key={matches['us-house'].key}
                contest={matches['us-house']}
                slug={'us-house'}
                point={coordinates}
                contestTitle={determineBallotTitle(matches['us-house'])}
                matchingNote={matchNotes['us-house']}
                renderParty={party => (
                  <EachPartyColumn
                    partyName={party.key}
                    partyBallot={party}
                    renderCandidate={candidate => (
                      <CandidateRow
                        ballotName={candidate.candidate_name}
                        party={candidate.party}
                        isIncumbent={candidate.incumbent}
                      />
                    )}
                  />
                )}
              />
              {matches['tx-senate'] && (
                <Ballots
                  key={matches['tx-senate'].key}
                  contest={matches['tx-senate']}
                  slug={'texas-senate'}
                  point={coordinates}
                  contestTitle={determineBallotTitle(matches['tx-senate'])}
                  matchingNote={matchNotes['tx-senate']}
                  renderParty={party => (
                    <EachPartyColumn
                      partyName={party.key}
                      partyBallot={party}
                      renderCandidate={candidate => (
                        <CandidateRow
                          ballotName={candidate.candidate_name}
                          party={candidate.party}
                          isIncumbent={candidate.incumbent}
                        />
                      )}
                    />
                  )}
                />
              )}
              <Ballots
                key={matches['tx-house'].key}
                slug={'texas-house'}
                point={coordinates}
                contest={matches['tx-house']}
                contestTitle={determineBallotTitle(matches['tx-house'])}
                matchingNote={matchNotes['tx-house']}
                renderParty={party => (
                  <EachPartyColumn
                    partyName={party.key}
                    partyBallot={party}
                    renderCandidate={candidate => (
                      <CandidateRow
                        ballotName={candidate.candidate_name}
                        party={candidate.party}
                        isIncumbent={candidate.incumbent}
                      />
                    )}
                  />
                )}
              />
              {matches['sboe'] && (
                <Ballots
                  key={matches['sboe'].key}
                  contest={matches['sboe']}
                  point={coordinates}
                  slug={'state-board-of-education'}
                  contestTitle={determineBallotTitle(matches['sboe'])}
                  matchingNote={matchNotes['sboe']}
                  renderParty={party => (
                    <EachPartyColumn
                      partyName={party.key}
                      partyBallot={party}
                      renderCandidate={candidate => (
                        <CandidateRow
                          ballotName={candidate.candidate_name}
                          party={candidate.party}
                          isIncumbent={candidate.incumbent}
                        />
                      )}
                    />
                  )}
                />
              )}
            </div>
          </Portal>
        )}
      </div>
    );
  }
}

FindYourDistricts.defaultProps = {
  onSelect: () => {},
};

export default FindYourDistricts;
